// ---------------------------------------------------------------------------
// Custom frames — the ONE builder of a custom component's sandboxed document
// (custom_code / custom_html), and the host side that sizes and themes it.
//
// Every surface renders a custom block through this module: the shared
// CustomHtmlRenderer (in-app viewer, Run view, downloadable runtime, designer
// canvas) and the static site (renderSite → customFrameHostScript). Before it
// there were four hand-built copies of the document, all with the same gaps:
//
//   - HEIGHT: the frame is sandboxed without allow-same-origin (an opaque
//     origin — no platform cookies, no parent DOM), so the parent cannot read
//     its scrollHeight; every block sat at its 60–80px minimum and clipped
//     what it drew. Now a ResizeObserver INSIDE the frame posts its height
//     and the host accepts it only from that frame's own contentWindow.
//   - THEME: the frame read --pg-* variables that do not exist in a separate
//     document. Now the host posts the page's --pg-* values and its Google
//     Fonts links in, and the frame applies them.
//   - LIBRARIES: `libs` loads chart/animation libraries from allow-listed
//     CDNs (the frame's own JS could add a script tag anyway — the sandbox is
//     the isolation; the list keeps authored pages on known hosts).
//   - DESIGN COMPONENTS: `component: { format: "dc", source }` renders a
//     Claude Design component verbatim with the live dc runtime
//     (./dc/dcRuntime.js — the same file the design importer captures with).
// ---------------------------------------------------------------------------
import { useEffect, useState, type RefObject } from 'react'
import dcRuntimeSource from './dc/dcRuntime.js?raw'
import type { CustomComponentSpec } from '@twasta/page-schema'
import { resolveTheme, themeCssVars } from './theme'

export type { CustomComponentSpec }

/** Hosts a custom block may load script libraries from. */
export const ALLOWED_LIB_HOSTS = ['cdnjs.cloudflare.com', 'cdn.jsdelivr.net', 'unpkg.com'] as const

export interface CustomFrameInput {
  id: string
  html?: string
  css?: string
  js?: string
  libs?: string[]
  component?: CustomComponentSpec
  /** The window.twasta bridge SDK (live or the static stub), when enabled. */
  sdk?: string
}

const MIN_H = 20
const MAX_H = 20000

export function clampFrameHeight(h: unknown): number | null {
  const n = typeof h === 'number' ? h : Number(h)
  if (!Number.isFinite(n) || n <= 0) return null
  return Math.min(MAX_H, Math.max(MIN_H, Math.ceil(n)))
}

/** True for an https script URL on an allow-listed CDN host. */
export function allowedLib(url: string): boolean {
  try {
    const u = new URL(url)
    return u.protocol === 'https:' && (ALLOWED_LIB_HOSTS as readonly string[]).includes(u.hostname)
  } catch {
    return false
  }
}

/** A string safe inside an inline <script>: no early `</script`. */
function scriptSafe(js: string): string {
  return (js || '').replace(/<\/(script)/gi, '<\\/$1').replace(/<!--/g, '<\\!--')
}

/** JSON safe inside an inline <script>. */
function jsonForScript(v: unknown): string {
  return JSON.stringify(v ?? null)
    .replace(/</g, '\\u003c').replace(/>/g, '\\u003e').replace(/\u2028/g, '\\u2028').replace(/\u2029/g, '\\u2029')
}

/** A .dc.html source ready to be parsed: the editor's support.js dropped
 *  (our runtime is inlined), control tags turned into <template data-sc>
 *  so an <sc-for> inside a <tbody> stays in the table. */
export function prepDcSource(src: string): string {
  return (src || '')
    .replace(/<script\b[^>]*\bsrc\s*=\s*["']\.?\/?support\.js["'][^>]*>\s*<\/script>/gi, '')
    .replace(/<sc-(for|if)\b/gi, '<template data-sc="$1"')
    .replace(/<\/sc-(?:for|if)\s*>/gi, '</template>')
    .replace(/<dc-import\b/gi, '<template data-sc="import"')
    .replace(/<\/dc-import\s*>/gi, '</template>')
}

/** The script INSIDE every frame: report height, apply the host's theme. */
function frameClientScript(id: string): string {
  return `(function(){
  var id = ${jsonForScript(id)}, last = 0;
  function post(){
    var b = document.body;
    if (!b) return;
    var r = b.getBoundingClientRect();
    var h = Math.ceil(Math.max(r.height + r.top, b.scrollHeight));
    if (h === last) return;
    last = h;
    try { parent.postMessage({ __pgFrame: 1, kind: 'size', id: id, h: h }, '*'); } catch (e) {}
  }
  function watch(){
    try { new ResizeObserver(post).observe(document.body); } catch (e) {}
    post();
  }
  if (document.body) watch(); else document.addEventListener('DOMContentLoaded', watch);
  window.addEventListener('load', function(){ post(); setTimeout(post, 300); setTimeout(post, 1500); });
  window.addEventListener('message', function(ev){
    var d = ev && ev.data;
    if (!d || d.__pgFrame !== 1 || ev.source !== parent || d.kind !== 'theme') return;
    var st = document.getElementById('pg-frame-theme');
    if (!st) { st = document.createElement('style'); st.id = 'pg-frame-theme'; document.head.insertBefore(st, document.head.firstChild); }
    st.textContent = ':root{' + String(d.css || '').replace(/[<{}]/g, '') + '}';
    var fonts = d.fonts || [];
    for (var i = 0; i < fonts.length; i++) {
      var href = String(fonts[i]);
      if (!/^https:\\/\\/fonts\\.googleapis\\.com\\//.test(href)) continue;
      if (document.querySelector('link[data-pg-font="' + href.replace(/"/g, '') + '"]')) continue;
      var l = document.createElement('link'); l.rel = 'stylesheet'; l.href = href; l.setAttribute('data-pg-font', href);
      document.head.appendChild(l);
    }
    post();
  });
})();`
}

// Base look of every frame: the PAGE's font, size and text colour (the host
// supplies the --pg-* values), a transparent background so the band behind
// shows through.
const FRAME_BASE_CSS =
  'html,body{margin:0;padding:0;background:transparent;}'
  + 'body{font-family:var(--pg-font,system-ui,sans-serif);font-size:var(--pg-base-size,16px);'
  + 'line-height:var(--pg-line-height,1.5);color:var(--pg-text,inherit);}'

function libTags(libs?: string[]): string {
  return (libs || []).filter(allowedLib)
    .map((u) => `<script src="${u.replace(/"/g, '&quot;')}"></script>`).join('')
}

/** The whole srcdoc of a custom block's frame. */
export function buildCustomFrameDoc(input: CustomFrameInput): string {
  const head = `<meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">`
    + `<base target="_top"><style>${FRAME_BASE_CSS}</style>`
    + `<script>${frameClientScript(input.id)}</script>`
  const sdk = input.sdk ? `<script>${scriptSafe(input.sdk)}</script>` : ''

  if (input.component && input.component.format === 'dc' && input.component.source) {
    const c = input.component
    let src = prepDcSource(c.source)
    if (!/<head[\s>]/i.test(src)) {
      src = `<!doctype html><html><head></head><body>${src}</body></html>`
    }
    const children: Record<string, string> = {}
    for (const [name, s] of Object.entries(c.children || {})) children[name] = prepDcSource(s)
    const setup = `<script>window.__DC_PROPS__=${jsonForScript(c.props || {})};`
      + `window.__DC_CHILDREN__=${jsonForScript(children)};</script>`
    const inject = head + setup + libTags(input.libs) + sdk
      + `<script>${scriptSafe(dcRuntimeSource)}</script>`
      + (input.css ? `<style>${input.css}</style>` : '')
    return src.replace(/<head([^>]*)>/i, (m) => m + inject)
      + (input.js ? `<script>${scriptSafe(input.js)}</script>` : '')
  }

  return `<!doctype html><html><head>${head}${libTags(input.libs)}<style>${input.css || ''}</style></head>`
    + `<body>${input.html || ''}${sdk}<script>${scriptSafe(input.js || '')}</script></body></html>`
}

// ── Host side ──────────────────────────────────────────────────────────────

let THEME_VAR_NAMES: string[] | null = null

/** Every --pg-* variable a page theme emits (read once from the theme). */
export function themeVarNames(): string[] {
  if (!THEME_VAR_NAMES) {
    THEME_VAR_NAMES = Object.keys(themeCssVars(resolveTheme(undefined)) as Record<string, unknown>)
      .filter((k) => k.startsWith('--pg-'))
  }
  return THEME_VAR_NAMES
}

/** The page theme as the frame needs it: `--pg-x:value;` declarations read
 *  off the frame element (it sits inside the themed page) + the page's
 *  Google Fonts stylesheets. */
export function frameThemePayload(frame: HTMLElement): { css: string; fonts: string[] } {
  const cs = getComputedStyle(frame)
  const decl: string[] = []
  for (const name of themeVarNames()) {
    const v = cs.getPropertyValue(name).trim()
    if (v) decl.push(`${name}:${v}`)
  }
  const fonts = Array.from(frame.ownerDocument.querySelectorAll<HTMLLinkElement>('link[href^="https://fonts.googleapis.com/css"]'))
    .map((l) => l.href)
  return { css: decl.join(';'), fonts: Array.from(new Set(fonts)) }
}

/** React host: auto-height from the frame's own reports + the theme posted
 *  in on every load. Returns the height to give the iframe (or the fixed
 *  height when the block sets one). */
export function useCustomFrame(frameRef: RefObject<HTMLIFrameElement | null>, opts: {
  id: string
  fixedHeight?: number
  doc: string
}): number | undefined {
  const [height, setHeight] = useState<number | undefined>(undefined)
  useEffect(() => {
    const el = frameRef.current
    if (!el) return
    const onMessage = (ev: MessageEvent) => {
      const d = ev.data as { __pgFrame?: number; kind?: string; id?: string; h?: unknown } | null
      if (!d || d.__pgFrame !== 1 || d.kind !== 'size' || ev.source !== el.contentWindow) return
      const h = clampFrameHeight(d.h)
      if (h !== null) setHeight(h)
    }
    const onLoad = () => {
      try {
        el.contentWindow?.postMessage({ __pgFrame: 1, kind: 'theme', ...frameThemePayload(el) }, '*')
      } catch { /* a frame torn down mid-load */ }
    }
    window.addEventListener('message', onMessage)
    el.addEventListener('load', onLoad)
    return () => {
      window.removeEventListener('message', onMessage)
      el.removeEventListener('load', onLoad)
    }
  }, [frameRef, opts.doc])
  return opts.fixedHeight && opts.fixedHeight > 0 ? opts.fixedHeight : height
}

/** The static site's vanilla twin of useCustomFrame, for every
 *  `iframe[data-pg-frame]` on the page (no React there). */
export function customFrameHostScript(): string {
  return `(function(){
  var NAMES = ${jsonForScript(themeVarNames())};
  function frames(){ return document.querySelectorAll('iframe[data-pg-frame]'); }
  function themeFor(el){
    var cs = getComputedStyle(el), out = [];
    for (var i = 0; i < NAMES.length; i++) { var v = cs.getPropertyValue(NAMES[i]).trim(); if (v) out.push(NAMES[i] + ':' + v); }
    var links = document.querySelectorAll('link[href^="https://fonts.googleapis.com/css"]'), fonts = [];
    for (var j = 0; j < links.length; j++) fonts.push(links[j].href);
    return { __pgFrame: 1, kind: 'theme', css: out.join(';'), fonts: fonts };
  }
  window.addEventListener('message', function(ev){
    var d = ev && ev.data;
    if (!d || d.__pgFrame !== 1 || d.kind !== 'size') return;
    var fs = frames();
    for (var i = 0; i < fs.length; i++) {
      if (fs[i].contentWindow !== ev.source) continue;
      if (fs[i].getAttribute('data-pg-fixed')) return;
      var h = Math.min(${MAX_H}, Math.max(${MIN_H}, Math.ceil(Number(d.h) || 0)));
      if (h) fs[i].style.height = h + 'px';
      return;
    }
  });
  function bind(el){
    if (el.__pgFrameBound) return; el.__pgFrameBound = true;
    var push = function(){ try { el.contentWindow.postMessage(themeFor(el), '*'); } catch (e) {} };
    el.addEventListener('load', push);
    push();
  }
  function all(){ var fs = frames(); for (var i = 0; i < fs.length; i++) bind(fs[i]); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', all); else all();
})();`
}
